import { QueryClient, type Query } from "@tanstack/react-query";

/**
 * AD-686. Клиент приложения не перечитывает запрос при монтировании
 * (`refetchOnMount: false` ниже). Поэтому сброс ключа (`invalidateQueries`) не
 * доходит до экрана, которого в момент сброса нет на странице: ключ помечен
 * устаревшим, но, когда человек возвращается на экран, тот показывает прежние
 * данные. Так шапка карточки события показывала явку до ручной отметки.
 *
 * Хук, до экрана которого сброс обязан доходить, ставит этот предикат: при
 * монтировании перечитать сброшенное — и только его. Давность по времени повода
 * не даёт, как и везде в приложении.
 */
export const refetchOnMountIfInvalidated = (
  query: Pick<Query, "state">,
): boolean => query.state.isInvalidated;

/**
 * Клиент React Query приложения (PROMPT_10: кэширование и производительность).
 * Вынесен из `App.tsx`, чтобы настройку можно было проверить тестом.
 */
export function createAppQueryClient(): QueryClient {
  return new QueryClient({
    defaultOptions: {
      queries: {
        staleTime: 5 * 60 * 1000, // 5 минут
        cacheTime: 10 * 60 * 1000, // 10 минут (v4)
        refetchOnWindowFocus: false,
        refetchOnReconnect: true,
        refetchOnMount: false,
        retry: 1,
        retryDelay: (attemptIndex: number) =>
          Math.min(1000 * 2 ** attemptIndex, 30000),
        onError: (error: unknown) => {
          const err = error as {
            response?: { status?: number };
            details?: { status?: number };
          };
          const status = err?.response?.status ?? err?.details?.status;
          if (status !== 401 && status !== 404) {
            console.error("Query error:", error);
          }
        },
      },
      // AD-274 (решение 15.09.2026): мутации сам клиент не повторяет — повтор
      // как по умолчанию в библиотеке (0). Сервер ключей идемпотентности от
      // фронта не принимает, и повтор после потерянного ответа создавал бы
      // второе событие, участника или письмо. Повторяет человек.
    },
  });
}
